import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, CheckCircle, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GHL_BOOKING_URL = 'https://api.leadconnectorhq.com/widget/booking/2Sa395sv0O6JZE7GBfpf';

const serviceOptions = [
  { id: 'svc-empty', value: '', label: 'Select a service...' },
  { id: 'svc-funnel', value: 'funnel_design', label: 'Funnel Design' },
  { id: 'svc-automation', value: 'marketing_automation', label: 'Marketing Automation' },
  { id: 'svc-leadgen', value: 'lead_generation', label: 'Lead Generation Systems' },
  { id: 'svc-conversion', value: 'conversion_optimization', label: 'Conversion Optimization' },
  { id: 'svc-full', value: 'full_package', label: 'Full Growth Engine Package' },
];

// Contact Form Component
function ContactForm({ onSubmitSuccess }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    service_interest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Message sent successfully!', { description: "We'll be in touch within 24 hours." });
        onSubmitSuccess();
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Form submission error:', error);
      }
      toast.error('Something went wrong', { description: 'Please try again or book a call directly.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="glass rounded-2xl p-8 border border-white/10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm text-gray-400 mb-2">First Name *</label>
          <input
            data-testid="form-first-name"
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required
            className="input-dark w-full"
            placeholder="John"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-2">Last Name *</label>
          <input
            data-testid="form-last-name"
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            required
            className="input-dark w-full"
            placeholder="Doe"
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm text-gray-400 mb-2">Email *</label>
        <input
          data-testid="form-email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="input-dark w-full"
          placeholder="john@company.com"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm text-gray-400 mb-2">Phone</label>
          <input
            data-testid="form-phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="input-dark w-full"
            placeholder="+1 (555) 000-0000"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-2">Company</label>
          <input
            data-testid="form-company"
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="input-dark w-full"
            placeholder="Your Company"
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm text-gray-400 mb-2">Service Interest</label>
        <select
          data-testid="form-service"
          name="service_interest"
          value={formData.service_interest}
          onChange={handleChange}
          className="input-dark w-full"
        >
          {serviceOptions.map((option) => (
            <option key={option.id} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <div className="mb-6">
        <label className="block text-sm text-gray-400 mb-2">Message *</label>
        <textarea
          data-testid="form-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={4}
          className="input-dark w-full resize-none"
          placeholder="Tell us about your business and marketing goals..."
        />
      </div>

      <button
        data-testid="form-submit"
        type="submit"
        disabled={isSubmitting}
        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Sending...</span>
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            <span>Send Message</span>
          </>
        )}
      </button>
    </form>
  );
}

// Success Message Component
function SuccessMessage({ onReset }) {
  return (
    <div className="glass rounded-2xl p-12 text-center border border-cyan-500/20">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', duration: 0.5 }}
        className="w-20 h-20 mx-auto rounded-full bg-cyan-500/20 flex items-center justify-center mb-6"
      >
        <CheckCircle className="w-10 h-10 text-cyan-400" />
      </motion.div>
      <h3 className="text-2xl font-semibold text-white mb-2">Message Received!</h3>
      <p className="text-gray-400 mb-6">We'll review your inquiry and get back to you within 24 hours.</p>
      <button onClick={onReset} className="text-cyan-400 text-sm hover:text-cyan-300 transition-colors">
        Send another message
      </button>
    </div>
  );
}

// Booking Widget Component
function BookingWidget() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="glass rounded-2xl overflow-hidden border border-white/10"
    >
      <iframe
        data-testid="booking-widget"
        src={GHL_BOOKING_URL}
        style={{ width: '100%', height: '700px', border: 'none', overflow: 'hidden' }}
        scrolling="no"
        title="Book a Strategy Call"
      />
    </motion.div>
  );
}

export default function CTASection() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showBooking, setShowBooking] = useState(false);

  const handleFormSuccess = useCallback(() => {
    setIsSubmitted(true);
  }, []);

  const handleReset = useCallback(() => {
    setIsSubmitted(false);
  }, []);

  return (
    <section 
      id="contact"
      data-testid="cta-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#050808] to-[#030303]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(0,240,255,0.08)_0%,_transparent_50%)]" />
      
      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4 block">Get Started</span>
          <h2 data-testid="cta-headline" className="text-4xl md:text-5xl font-bold text-white mb-6">
            Build Your
            <br />
            <span className="gradient-text-cyan">Growth Engine</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-base">
            Let's design a marketing system tailored to your business. Start with a free strategy call.
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex justify-center gap-4 mb-12"
        >
          <button
            data-testid="toggle-form-btn"
            onClick={() => setShowBooking(false)}
            className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
              !showBooking ? 'bg-white text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            Send a Message
          </button>
          <button
            data-testid="toggle-booking-btn"
            onClick={() => setShowBooking(true)}
            className={`px-6 py-3 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
              showBooking ? 'bg-white text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <Calendar className="w-4 h-4" />
            Book a Strategy Call
          </button>
        </motion.div>
        
        <div className="max-w-2xl mx-auto">
          {!showBooking ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              {isSubmitted ? (
                <SuccessMessage onReset={handleReset} />
              ) : (
                <ContactForm onSubmitSuccess={handleFormSuccess} />
              )}
            </motion.div>
          ) : (
            <BookingWidget />
          )}
        </div>
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-gray-500 text-xs">🔒 Your information is secure and will never be shared</p>
        </motion.div>
      </div>
    </section>
  );
}
